import { useEffect, useMemo, useRef, useState } from 'react';
import { MIN_CLIP_LENGTH, type ReelClip } from '../core/clips';
import { formatClock, formatSeconds, formatTotal, formatVideoTime, periodName } from '../core/time';
import { useThumbnails } from '../media/thumbs';
import { useReel } from '../state/reel';
import { useApp } from '../state/store';
import { Button, ClipRow, Field, Panel, ScoreBug, type Tone } from '../ui';
import { Player, type PlayerHandle } from '../ui/Player';
import { useHotkeys } from '../ui/hotkeys';

const newId = () => `u${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

function metaOf(c: ReelClip) {
  const range = `${formatVideoTime(c.start)} → ${formatVideoTime(c.end)}`;
  if (c.origin === 'list' && c.period && c.clockSec !== undefined) return `${periodName(c.period)} ${formatClock(c.clockSec)} · ${range}`;
  return range;
}

function tagsOf(c: ReelClip): { label: string; tone?: Tone }[] {
  const t: { label: string; tone?: Tone }[] = [];
  if (c.origin === 'manual') t.push({ label: 'Added by you', tone: 'cue' });
  if (c.origin === 'mark') t.push({ label: 'Marked live', tone: 'cue' });
  if (c.sourceIds.length > 1) t.push({ label: `${c.sourceIds.length} timestamps merged` });
  if (c.edited) t.push({ label: 'Edited' });
  if (c.outside) t.push({ label: 'After the video ends', tone: 'warning' });
  return t;
}

export function ClipsScreen() {
  const { project, update, updateEdits, setStep, url, file, info } = useApp();
  const { clips, stats, unmapped } = useReel();
  const player = useRef<PlayerHandle>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(clips[0]?.id ?? null);
  const [now, setNow] = useState(0);
  const duration = info?.duration ?? 0;
  const sel = clips.find((c) => c.id === selectedId) ?? clips[0];
  const thumbs = useThumbnails(file, useMemo(() => clips.map((c) => c.start), [clips]));

  useEffect(() => {
    if (!selectedId && clips[0]) setSelectedId(clips[0].id);
  }, [clips, selectedId]);

  const select = (c: ReelClip | undefined, play = false) => {
    if (!c) return;
    setSelectedId(c.id);
    if (play) player.current?.playRange(c.start, c.end);
    else player.current?.seek(c.start);
    document.getElementById(`clip-${c.id}`)?.scrollIntoView({ block: 'nearest' });
  };

  const setBounds = (c: ReelClip, start: number, end: number) => {
    start = Math.max(0, start);
    if (duration) end = Math.min(duration, end);
    if (end - start < MIN_CLIP_LENGTH) return;
    updateEdits((e) => ({ ...e, bounds: { ...e.bounds, [c.id]: { start, end } } }));
  };
  const toggle = (c: ReelClip) => updateEdits((e) => ({ ...e, excluded: { ...e.excluded, [c.id]: c.included } }));
  const isOwn = (c: ReelClip) => c.origin !== 'list';
  const remove = (c: ReelClip) => {
    const i = clips.findIndex((x) => x.id === c.id);
    update((p) => ({ marks: p.marks.filter((m) => m.id !== c.id) }));
    setSelectedId(clips[i + 1]?.id ?? clips[i - 1]?.id ?? null);
  };
  const addHere = () => {
    const t = player.current?.time() ?? now;
    const id = newId();
    update((p) => ({ marks: [...p.marks, { id, origin: 'manual' as const, start: Math.max(0, t - 8), end: Math.min(duration || t + 2, t + 2) }] }));
    setSelectedId(id);
  };

  const idx = sel ? clips.findIndex((c) => c.id === sel.id) : -1;
  useHotkeys({
    ' ': () => player.current?.toggle(),
    p: () => select(sel, true),
    ArrowDown: () => select(clips[idx + 1]),
    ArrowUp: () => select(clips[idx - 1]),
    ArrowLeft: () => player.current?.seek(player.current.time() - 1),
    ArrowRight: () => player.current?.seek(player.current.time() + 1),
    a: addHere,
    x: () => sel && toggle(sel),
  });

  if (!url) return null;
  const markers = Object.entries(project.kickoffs).map(([p, at]) => ({ at, label: `K${p}` }));

  return (
    <div className="app-grid-clips">
      <div className="app-sticky">
        <Player
          ref={player}
          url={url}
          duration={duration}
          compact
          clips={clips.map((c) => ({ start: c.start, end: c.end, state: c.id === sel?.id ? 'selected' : c.included ? 'included' : 'excluded' }))}
          markers={markers}
          onTime={setNow}
          topLeft={() => sel && <ScoreBug period={sel.period ? periodName(sel.period) : undefined} time={sel.clockSec !== undefined ? formatClock(sel.clockSec) : formatVideoTime(sel.start)} label={sel.title} />}
        >
          <div data-tour="addclip" className="app-row" style={{ gap: 8, flexWrap: "wrap" }}>
            <Button variant="primary" icon="plus" onClick={addHere} style={{ flexGrow: 1 }} kbd="A">
              Add clip here
            </Button>
            <Button variant="overlay" disabled={!sel} onClick={() => sel && setBounds(sel, Math.min(now, sel.end - MIN_CLIP_LENGTH), sel.end)}>
              Set start
            </Button>
            <Button variant="overlay" disabled={!sel} onClick={() => sel && setBounds(sel, sel.start, Math.max(now, sel.start + MIN_CLIP_LENGTH))}>
              Set end
            </Button>
          </div>
        </Player>

        {sel && (
          <Panel aria-label="Selected clip" style={{ gap: 12 }}>
            <div className="app-row-between">
              <span className="app-label">
                Clip {sel.index} · {formatSeconds(sel.end - sel.start)}
              </span>
              <Button size="sm" variant="ghost" icon="play" onClick={() => select(sel, true)} kbd="P">
                Play clip
              </Button>
            </div>
            <div className="app-row" style={{ alignItems: 'flex-end', gap: 8 }}>
              <Field
                key={sel.id}
                label="Label (optional)"
                placeholder="e.g. Goal"
                defaultValue={project.edits.labels[sel.id] ?? sel.label ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  updateEdits((ed) => ({ ...ed, labels: { ...ed.labels, [sel.id]: v } }));
                }}
                style={{ width: '100%' }}
                className="grow"
              />
              {isOwn(sel) ? (
                <Button icon="trash" onClick={() => remove(sel)}>
                  Delete
                </Button>
              ) : (
                <Button onClick={() => toggle(sel)} disabled={sel.outside}>
                  {sel.included ? 'Leave out' : 'Include'}
                </Button>
              )}
            </div>
            {sel.edited && (
              <button type="button" className="text-small" onClick={() => updateEdits((e) => { const b = { ...e.bounds }; delete b[sel.id]; return { ...e, bounds: b }; })} style={{ alignSelf: 'flex-start', background: 'none', border: 0, padding: 0, color: 'var(--link)', cursor: 'pointer' }}>
                Reset start and end
              </button>
            )}
          </Panel>
        )}

        <Panel data-tour="reelsum" aria-labelledby="reel-title" style={{ gap: 12 }}>
          <div className="app-row-between" style={{ alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <h2 id="reel-title" className="app-label">
                Your reel
              </h2>
              <span className="display-lg" style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatTotal(stats.total)}
              </span>
            </div>
            <span className="time text-small muted" style={{ paddingBottom: 6 }}>
              {stats.count} of {clips.length} clips
            </span>
          </div>
          <Button variant="primary" icon="arrow-right" iconAfter disabled={stats.count === 0} onClick={() => setStep('export')}>
            Continue to export
          </Button>
        </Panel>
      </div>

      <Panel aria-labelledby="clips-title" style={{ padding: '20px 12px 12px', gap: 12 }}>
        <div className="app-row-between" style={{ padding: '0 12px 4px', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h1 id="clips-title" className="app-title">
              Review clips
            </h1>
            <p className="text-small muted" style={{ margin: 0 }}>
              Click a clip to preview it. Untick to leave it out. ↑ ↓ move between clips, P plays, X leaves out, A adds a clip at the playhead.
            </p>
          </div>
          <Button onClick={() => updateEdits((e) => ({ ...e, excluded: {} }))}>Include all</Button>
        </div>
        {unmapped.length > 0 && (
          <div className="app-banner app-banner-warning" style={{ margin: '0 12px' }}>
            {unmapped.length} timestamps need a kickoff that isn't set yet.
            <Button size="sm" onClick={() => setStep('sync')}>
              Sync kickoffs
            </Button>
          </div>
        )}
        <div className="clip-list" ref={listRef} data-tour="cliplist">
          {clips.length === 0 && <p className="text-small muted" style={{ padding: 12 }}>No clips yet. Use “Add clip here” under the player.</p>}
          {clips.map((c) => (
            <div key={c.id} id={`clip-${c.id}`}>
              <ClipRow
                index={c.index}
                title={c.title}
                meta={metaOf(c)}
                duration={formatSeconds(c.end - c.start)}
                thumbnail={thumbs.get(Math.round(c.start))}
                included={c.included}
                selected={c.id === sel?.id}
                tags={tagsOf(c)}
                disabled={c.outside}
                onToggle={() => toggle(c)}
                onSelect={() => select(c)}
                onNudge={(edge, d) => (edge === 'start' ? setBounds(c, c.start + d, c.end) : setBounds(c, c.start, c.end + d))}
              />
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}
